import { Box, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';

import { ProjectRolesTable } from './ProjectRolesTable';
import { ProjectRatingsTable } from './ProjectRatingsTable';
import { InterviewCandidatesTable } from './InterviewCandidatesTable';
import type { AppraisalProjectGroup } from '@/api/types/appraisal.types';
import type { SectionAccess } from '@/auth/permissions.types';

const COMPLETE_STATUSES = new Set(['Finalised', 'Delivered']);

interface ProjectSubsectionProps {
  group: AppraisalProjectGroup;
  sectionAccess: SectionAccess | undefined;
  appraisalId: number;
  rowVersion?: string;
}

function calcDurationWeeks(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
}

export function ProjectSubsection({
  group,
  sectionAccess,
  appraisalId,
  rowVersion,
}: ProjectSubsectionProps) {
  const isComplete = COMPLETE_STATUSES.has(group.snapshotStatus);
  const durationWeeks = calcDurationWeeks(group.assignmentStartDate, group.assignmentEndDate);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MuiLink
          component={Link}
          to={`/job-ends/${group.jobEndId}`}
          variant="subtitle1"
          sx={{ fontWeight: 'bold' }}
        >
          {group.projectName}
        </MuiLink>
        <Typography variant="body2" color="text.secondary">
          Project Duration: <strong>{durationWeeks} weeks</strong>
        </Typography>
        {!isComplete && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            (not completed)
          </Typography>
        )}
      </Box>
      <Box sx={{ pt: 1 }}>
        {isComplete && (
          <>
            <ProjectRolesTable
              activityRatings={group.activityRatings}
              isComplete={isComplete}
            />
            <ProjectRatingsTable
              skillRatings={group.skillRatings}
              isComplete={isComplete}
              displayMode={sectionAccess?.displayMode}
            />
          </>
        )}
        <InterviewCandidatesTable
          candidates={group.interviewCandidates}
          sectionAccess={sectionAccess}
          appraisalId={appraisalId}
          rowVersion={rowVersion}
        />
      </Box>
    </Box>
  );
}

export default ProjectSubsection;
